import React, { useState, useEffect, useCallback } from 'react'
import { cn } from '../../lib/utils'
import { Slide } from '../presentation/Slide'
import { PresenterTimer } from './PresenterTimer'
import { NextSlidePreview } from './NextSlidePreview'
import { PresenterNotes } from './PresenterNotes'
import type { PresentationData, SlideTransition } from '../../types'

export interface PresenterModeProps {
  presentation: PresentationData
  currentSlideIndex: number
  onNextSlide: () => void
  onPreviousSlide: () => void
  onGoToSlide: (index: number) => void
  onExit: () => void
  defaultTransition?: SlideTransition
  className?: string
}

/**
 * PresenterMode Component
 *
 * Full-featured presenter mode with:
 * - Current slide (audience view)
 * - Presenter notes panel
 * - Next slide preview
 * - Timer
 * - Navigation controls
 */
export const PresenterMode: React.FC<PresenterModeProps> = ({
  presentation,
  currentSlideIndex,
  onNextSlide,
  onPreviousSlide,
  onGoToSlide,
  onExit,
  defaultTransition = 'slide',
  className,
}) => {
  const [showNotes, setShowNotes] = useState(true)
  const [showNextSlide, setShowNextSlide] = useState(true)
  const [showTimer, setShowTimer] = useState(true)

  const currentSlide = presentation.slides[currentSlideIndex]
  const nextSlide = presentation.slides[currentSlideIndex + 1] || null

  /**
   * Handle keyboard navigation specific to presenter mode
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
        case 'PageDown':
          e.preventDefault()
          onNextSlide()
          break

        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault()
          onPreviousSlide()
          break

        case 'Home':
          e.preventDefault()
          onGoToSlide(0)
          break

        case 'End':
          e.preventDefault()
          onGoToSlide(presentation.slides.length - 1)
          break

        case 'n':
        case 'N':
          e.preventDefault()
          setShowNotes(prev => !prev)
          break

        case 't':
        case 'T':
          e.preventDefault()
          setShowNextSlide(prev => !prev)
          break

        case 'Escape':
          e.preventDefault()
          onExit()
          break
      }
    },
    [onNextSlide, onPreviousSlide, onGoToSlide, onExit, presentation.slides.length]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return (
    <div className={cn('w-full h-screen bg-background', className)}>
      {/* Presenter Mode Layout */}
      <div className="w-full h-full grid grid-cols-1 lg:grid-cols-4 gap-4 p-4">
        {/* Left Panel - Notes & Controls */}
        {(showNotes || showNextSlide || showTimer) && (
          <div className="lg:col-span-1 flex flex-col gap-4 overflow-hidden">
            {/* Timer */}
            {showTimer && (
              <div className="flex-shrink-0 bg-card rounded-lg p-3 shadow-lg">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground uppercase">
                    Elapsed Time
                  </span>
                  <PresenterTimer
                    isRunning={true}
                    onTimeUpdate={() => {}}
                  />
                </div>
              </div>
            )}

            {/* Presenter Notes */}
            {showNotes && (
              <div className="flex-1 min-h-0">
                <PresenterNotes
                  notes={currentSlide?.notes}
                  slideTitle={currentSlide?.title}
                  slideDuration={currentSlide?.duration}
                />
              </div>
            )}

            {/* Next Slide Preview */}
            {showNextSlide && (
              <div className="flex-shrink-0">
                <NextSlidePreview nextSlide={nextSlide} />
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex-shrink-0 flex flex-wrap gap-2">
              <button
                onClick={() => setShowNotes(!showNotes)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded transition-colors',
                  showNotes
                    ? 'bg-primary-600 text-white'
                    : 'bg-muted text-foreground'
                )}
              >
                Notes
              </button>
              <button
                onClick={() => setShowNextSlide(!showNextSlide)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded transition-colors',
                  showNextSlide
                    ? 'bg-primary-600 text-white'
                    : 'bg-muted text-foreground'
                )}
              >
                Next
              </button>
              <button
                onClick={() => setShowTimer(!showTimer)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded transition-colors',
                  showTimer
                    ? 'bg-primary-600 text-white'
                    : 'bg-muted text-foreground'
                )}
              >
                Timer
              </button>
            </div>
          </div>
        )}

        {/* Right Panel - Current Slide (Audience View) */}
        <div className="lg:col-span-3 relative bg-card rounded-lg shadow-xl overflow-hidden">
          {/* Slide Counter */}
          <div className="absolute top-4 right-4 z-10 px-3 py-1 bg-primary-foreground/90 backdrop-blur rounded-full text-primary text-sm font-medium">
            {currentSlideIndex + 1} / {presentation.slides.length}
          </div>

          {/* Exit Button */}
          <button
            onClick={onExit}
            className="absolute top-4 left-4 z-10 px-4 py-2 bg-primary-foreground/90 backdrop-blur rounded-lg text-primary text-sm font-medium hover:bg-primary-foreground/80 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Exit Presenter Mode
          </button>

          {/* Current Slide */}
          <div className="w-full h-full">
            <Slide
              data={currentSlide}
              isActive={true}
              transition={currentSlide?.transition || defaultTransition}
              direction="forward"
            />
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={onPreviousSlide}
            disabled={currentSlideIndex === 0}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-card/90 backdrop-blur rounded-full shadow-lg hover:scale-110 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous slide"
          >
            <svg className="w-6 h-6 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={onNextSlide}
            disabled={currentSlideIndex === presentation.slides.length - 1}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-card/90 backdrop-blur rounded-full shadow-lg hover:scale-110 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next slide"
          >
            <svg className="w-6 h-6 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-primary-foreground/90 backdrop-blur rounded-lg text-primary text-xs">
        <span className="font-medium">Shortcuts:</span> ←→ Navigate | N: Notes | T: Next | ESC: Exit
      </div>
    </div>
  )
}

export default PresenterMode
